import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  listCampaigns: [],
  listTotalCount: 0,
  listCampaignType: null,
  listPage: 1,
  uiFlags: {
    isFetching: false,
    isPaginatedFetching: false,
    isCreating: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns: _state => campaignType => {
    // When paginated data is available for this type, always use it
    if (_state.listCampaignType === campaignType) {
      return _state.listCampaigns;
    }
    // While a paginated fetch is in-flight, return empty so the component shows
    // the loading state rather than all unfiltered records from a prior
    // non-paginated fetch.
    if (_state.uiFlags.isPaginatedFetching) {
      return [];
    }
    return _state.records.filter(
      record => record.campaign_type === campaignType
    );
  },
  getCampaignsListTotalCount: _state => _state.listTotalCount,
  getCampaignsListPage: _state => _state.listPage,
  getAllCampaigns: _state => {
    return _state.records;
  },
  getCampaignStatuses: _state => {
    if (!Array.isArray(_state.records)) {
      return [];
    }
    return _state.records.map(campaign => campaign.status).filter((status, index, self) => self.indexOf(status) === index);
  },
  isCampaignsLoading: _state => {
    return _state.uiFlags.isFetching;
  },
  hasCampaignData: _state => {
    return Array.isArray(_state.records) && _state.records.length > 0;
  },
};

export const actions = {
  get: async function getCampaigns({ commit }) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.get();
      commit(types.SET_CAMPAIGNS, response.data);
    } catch (error) {
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
  getPaginated: async function getCampaignsPaginated(
    { commit, state },
    { campaignType, page = 1, perPage = 20 }
  ) {
    // Use a dedicated flag so a concurrent non-paginated `get` never blocks page changes
    if (state.uiFlags.isPaginatedFetching) return;
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true, isPaginatedFetching: true });

    // Safeguard: clear loading if the request never completes (e.g. network hang)
    const LOADING_TIMEOUT_MS = 15000;
    const timeoutId = setTimeout(() => {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false, isPaginatedFetching: false });
    }, LOADING_TIMEOUT_MS);

    try {
      const response = await CampaignsAPI.getList(campaignType, page, perPage);
      const data = response.data || {};
      // Support both { campaigns, meta } and nested { data: { campaigns, meta } }
      const campaigns = data.campaigns ?? data.data?.campaigns ?? [];
      const totalCount = data.meta?.total_count ?? data.data?.meta?.total_count ?? 0;
      commit(types.SET_CAMPAIGNS_PAGINATED, {
        campaigns,
        totalCount,
        campaignType,
        page,
      });
      // Clear loading immediately when we have data so UI updates in same tick
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false, isPaginatedFetching: false });
    } catch (error) {
      commit(types.SET_CAMPAIGNS_PAGINATED, {
        campaigns: [],
        totalCount: 0,
        campaignType: campaignType || null,
        page: 1,
      });
    } finally {
      clearTimeout(timeoutId);
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false, isPaginatedFetching: false });
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, { id, displayId }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      // Use display_id for API call (backend finds by display_id)
      await CampaignsAPI.delete(displayId);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      // Use id for local store mutation (records stored by id)
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  fetchCampaignFilterOptions: async ({ commit }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    try {
      const response = await CampaignsAPI.getFilterOptions();
      commit(types.SET_CAMPAIGNS, response.data.campaigns);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
    }
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CAMPAIGN](_state, data) {
    MutationHelpers.create(_state, data);
    // Also prepend to the paginated list if it's loaded for the same campaign type
    if (_state.listCampaignType === data.campaign_type) {
      _state.listCampaigns = [data, ..._state.listCampaigns];
      _state.listTotalCount += 1;
    }
  },
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.SET_CAMPAIGNS_PAGINATED](_state, { campaigns, totalCount, campaignType, page }) {
    _state.listCampaigns = Array.isArray(campaigns) ? campaigns : [];
    _state.listTotalCount = totalCount || 0;
    _state.listCampaignType = campaignType || null;
    _state.listPage = page || 1;
  },
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
